import React, {Component, ReactElement, ReactNode, RefObject} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../../../components/shared/hoc/createAdminPage'
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import {ApplyHistoryInfo, OwnershipTransferDetail} from '../repository/NatGetOwnershipTransfer'
import {PageFooterArea, PageFotterAreaLeft, PageMainArea} from '../../../../components/shared/hoc/withAdminPageTemplate'
import {StyledTableCaption, TableRowName} from '../../../../styledComponents/adminPage/Table'
import {NatOwnershipTransferStatus, OwnershipTransferProcess} from './NatOwnershipTransferStatus'
import {NatOwnershipTransferApplyDetailByIntermediary} from './NatOwnershipTransferApplyDetailByIntermediary'
import {NatOwnershipTransferApplyDetailByOldMember} from './NatOwnershipTransferApplyDetailByOldMember'
import {NatOwnershipTransferApplyDetailByNewMember} from './NatOwnershipTransferApplyDetailByNewMember'
import {HeadingAreaMessageParams} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {BackLink, PrintLink} from '../../../../styledComponents/adminPage/Links'
import {APPLY_CATEGORY, OWNERSHIP_TRANSFER_STATUS} from '../../../../CodeMaster'
import {Box, Table, TableBody, TableRow} from '@mui/material'
import AuthorityChecker from '../../../../components/AuthorityChecker'
import {HKConstants} from '../../../../HKConstants'
import {RightRowFlexContainer} from '../../../../styledComponents/FlexComponents'
import ReactToPrint from 'react-to-print'
import {PrintContentArea} from '../../applications/common/admin/print/NatAdminApplicatioPrintCommonStyle'
import {StyledTableCell} from '../../applications/common/admin/NatAdminApplicationCommon'
import moment from 'moment'
import {convertToRoomNumberForResident} from '../../../common/util/Natutil'
import {NatOwnershipTransferDocument} from './print/NatOwnershipTransferDocument'
import {Location} from 'react-router-dom'


interface Props {
    condominiumCd: string
    applyId: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    ownershipTransferDetail: Nullable<OwnershipTransferDetail>
    isLoadCompleted: boolean
}

class NatOwnershipTransferCompletedPage extends Component<Props & AdminPagePropsSet, States> {
    componentRef: RefObject<HTMLDivElement>

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.state = {
            ownershipTransferDetail: null,
            isLoadCompleted: false,
        }
        this.componentRef = React.createRef()

    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const ownershipTransferDetail = await this.getOwnershipTransfer()
            this.setState({ownershipTransferDetail: ownershipTransferDetail})
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
        this.showInitialMessage()
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    async getOwnershipTransfer(): Promise<Nullable<OwnershipTransferDetail>> {
        const {condominiumCd, applyId, ownershipTransfersRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            ownershipTransfersRepo.getOwnershipTransfer.bind(ownershipTransfersRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    convertToOwnershipTransferProcess(applyHistoryInfoList: ApplyHistoryInfo[]): OwnershipTransferProcess[] {
        const completeInfo = applyHistoryInfoList
            .find(history => history.applyStatus === OWNERSHIP_TRANSFER_STATUS.COMPLETE.value)

        if (!completeInfo) {
            return []
        }

        const completeProcess = {
            processName: '完了',
            rep: [],
            processLatestDateTime: completeInfo.registerDatetime,
            completed: true,
        }
        return [completeProcess]
    }

    renderApplyStatus(): Nullable<ReactElement> {
        if (!this.state.ownershipTransferDetail) {
            return <></>
        }
        const {applyHistoryInfo} = this.state.ownershipTransferDetail

        return (
            <>
                <Table>
                    <StyledTableCaption>進行状況</StyledTableCaption>
                </Table>
                <NatOwnershipTransferStatus
                    process={this.convertToOwnershipTransferProcess(applyHistoryInfo)}
                />
            </>
        )
    }

    renderApplyTable(): Nullable<ReactElement> {
        if (!this.state.ownershipTransferDetail) {
            return <></>
        }
        const {
            intermediaryInfo,
            oldMemberInfo,
            newMemberInfo,
            applyId,
            condominiumName,
            ridgeName,
            roomNumber,
            applyHistoryInfo,
        } = this.state.ownershipTransferDetail

        const completeDatetime = applyHistoryInfo
            .find(history => history.applyStatus === OWNERSHIP_TRANSFER_STATUS.COMPLETE.value)?.registerDatetime ?? ''

        return (
            <>
                <Table>
                    <StyledTableCaption>申請内容</StyledTableCaption>
                    <TableRow>
                        <TableRowName>申請ID</TableRowName>
                        <StyledTableCell colSpan={4}>
                            {applyId}
                        </StyledTableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>申請区分</TableRowName>
                        <StyledTableCell colSpan={4}>
                            {APPLY_CATEGORY.OWNERSHIP_TRANSFER.label}
                        </StyledTableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>申請者</TableRowName>
                        <StyledTableCell colSpan={4}>
                            {condominiumName}　{ridgeName} {convertToRoomNumberForResident(roomNumber)}
                        </StyledTableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>申請日</TableRowName>
                        <StyledTableCell colSpan={4}>
                            {moment(completeDatetime).format('YYYY年M月D日 H時m分')}
                        </StyledTableCell>
                    </TableRow>
                </Table>
                <Table>
                    <TableBody>
                        <NatOwnershipTransferApplyDetailByIntermediary
                            intermediaryInfo={intermediaryInfo}
                        />
                        <NatOwnershipTransferApplyDetailByOldMember
                            oldMemberInfo={oldMemberInfo}
                        />
                        <NatOwnershipTransferApplyDetailByNewMember
                            newMemberInfo={newMemberInfo}
                        />
                    </TableBody>
                </Table>
            </>
        )
    }

    renderPageHeaderArea(): ReactNode {
        return (
            <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.APPLICATION_PRINT_OWNERSHIP_TRANSFER}>
                <Box mt={'20px'}>
                    <RightRowFlexContainer>
                        <ReactToPrint
                            trigger={() => (
                                <PrintLink>
                                    <Box ml={'10px'}>
                                        印刷する
                                    </Box>
                                </PrintLink>
                            )}
                            content={() => this.componentRef.current}/>
                        <PrintContentArea>
                            <div ref={this.componentRef}>
                                {this.state.ownershipTransferDetail &&
                                    <NatOwnershipTransferDocument
                                        ownershipTransferDocumentParam={this.state.ownershipTransferDetail}
                                    />
                                }
                            </div>
                        </PrintContentArea>
                    </RightRowFlexContainer>
                </Box>
            </AuthorityChecker>
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return <></>
        }

        return <>
            <Box mb={'20px'}>
                {this.renderPageHeaderArea()}
            </Box>
            <PageMainArea>
                {this.renderApplyTable()}
                {this.renderApplyStatus()}
            </PageMainArea>
            <PageFooterArea>
                <PageFotterAreaLeft>
                    <BackLink
                        onClick={() => this.props.navigate(
                            `/admin/applications`,
                        )}
                    >
                        申請状況一覧に戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </PageFooterArea>
        </>
    }
}

export default createAdminPage(NatOwnershipTransferCompletedPage)
